import { useCallback, useEffect, useState } from "react"
import { merge, replace, isEmpty, find, noop } from "lodash"
import classNames from "classnames"
import PropTypes from "prop-types"
import { Box, Paper, Button, Grow, Typography, Alert, Grid2 as Grid } from "@mui/material"
import { isMatchingContributor, getDecimals } from "modules/rebalance/helpers"
import UnifiedSearch from "modules/unified-search"
import SelectedPosition from "components/selected-position"
import { buildContributorKey } from "helpers/contributors"
import { getRealValue } from "components/factor-constraint-input/helpers"
import { limitPrecision } from "helpers/math"
import { CONTRIBUTOR, CONTRIBUTOR_WITH_ADV } from "helpers/shapes/simulator"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import { FIELDS } from "helpers/simulator"
import OPTooltip from "components/material/tooltip"
import InputWidget from "./input-widget"

import "./styles.scss"

// When rebalancing, if the value entered equals the value stored in the selectedPosition, it is a 0 trade
function isZero(value, field, selectedPosition) {
  let initialValue = selectedPosition[field]
  const trimmedValue = replace(value, /\s/gi, "")
  let parsedValue = parseFloat(trimmedValue)
  let decimals = getDecimals(parsedValue)

  if (field === FIELDS.economicExposure) {
    parsedValue = parseFloat(getRealValue(trimmedValue))
    decimals = 0
  } else {
    // percentages are stored on a 1 basis so we need to multiply by 100
    initialValue *= 100
  }

  // Zero is a valid value if the initialValue is not 0
  return (parsedValue === 0 && !initialValue) || limitPrecision(parsedValue, decimals) === limitPrecision(initialValue, decimals)
}

export default function NewTrade({ account, modelId, isActive, newSimulateAction, contributors, selectSearchItem, selectedPositionWithAdv, currentGrouping, workingEquity, isActiveTradingDisabled, isRunning, tradeDisabledReason, Summarizer, externalState, onReset = noop, onAddTradeClicked = noop, hideEconomicExposure, tradeMessage }) {
  const [selectedPosition, setSelectedPosition] = useState(null)
  const [value, setValue] = useState("")
  const [field, setField] = useState(FIELDS.economicExposure)
  const [visible, setVisible] = useState(false)
  const [tradeDisabled, setTradeDisabled] = useState(true)

  // Verify if the trade is valid
  useEffect(() => {
    if (!selectedPosition) {
      setTradeDisabled(true)
      return
    }
    if (isZero(value, field, selectedPosition)) {
      setTradeDisabled(true)
      return
    }
    setTradeDisabled(false)
  }, [value, field, selectedPosition])

  const reset = useCallback(() => {
    setVisible(false)
    setSelectedPosition(null)
    setValue("")
    setField(FIELDS.economicExposure)
    onReset()
  }, [onReset])

  // Reset trade UI if the user rebalances via the table
  useEffect(() => {
    if (isRunning && visible) {
      reset()
    }
  }, [isRunning, reset, visible])

  function addTrade() {
    newSimulateAction(selectedPosition, field, value, !selectedPosition?.isInPositionSet, true)
    reset()
  }

  const selectPosition = useCallback((searchItem) => {
    let matchingContributor
    // We assume that when a selected position is provided, then it's a contributor. If not, it's a search item.
    // The structure of the items aren't the same, so we need to handle it differently.
    if (externalState?.selectedPosition) {
      matchingContributor = find(contributors, contributor => buildContributorKey(contributor) === buildContributorKey(searchItem))
    } else {
      matchingContributor = find(contributors, contributor => isMatchingContributor(searchItem, contributor))
    }
    const position = {
      ...searchItem,
      ...matchingContributor,
      isInPositionSet: !isEmpty(matchingContributor),
    }

    // Preselect activePercentEquity only if active and not rebalancing
    if (isActive && !position.isInPositionSet && position.activePercentEquity) {
      setField(FIELDS.activePercentEquity)
      setValue(position.activePercentEquity)
    }
    // Set the field to economicExposure if activePercentEquity will not be dispalyed
    if (field === FIELDS.activePercentEquity && (!isActive || !position.activePercentEquity)) {
      setField(FIELDS.economicExposure)
      setValue("")
    }

    if (selectSearchItem) {
      selectSearchItem(searchItem)
    }
    setSelectedPosition(position)
  }, [contributors, isActive, field, selectSearchItem, externalState])

  useEffect(() => {
    if (externalState) {
      setVisible(true)
      const { selectedPosition, tradedPositionField } = externalState
      if (tradedPositionField) {
        // TODO: First part of tradePositionField is the row but changing it externally is not supported yet
        const [, field] = tradedPositionField
        setField(field)
      }
      selectPosition(selectedPosition)
    }
  }, [externalState, selectPosition])

  function toggleVisible() {
    onAddTradeClicked()
    if (!visible) {
      reset()
    }
    setVisible(!visible)
  }

  let mergedPositionWithAdv = selectedPosition
  if (selectedPositionWithAdv && selectedPosition && buildContributorKey(selectedPositionWithAdv) === buildContributorKey(selectedPosition)) {
    mergedPositionWithAdv = merge(selectedPosition, selectedPositionWithAdv)
  }

  let cta = <Button data-test-id="add-trade-button" variant="contained" color="primary" disabled={Boolean(tradeDisabledReason)} onClick={toggleVisible}>Add trade</Button>
  if (tradeDisabledReason) {
    cta = <OPTooltip title={tradeDisabledReason}><span>{cta}</span></OPTooltip>
  }

  return <div className={classNames("simulator-new-trade", { visible })}>
    {cta}
    <Grow in={visible} style={{ transformOrigin: "0 0 0" }}>
      <Paper square>
        <Box className="header">
          <Typography variant="h2">Add Trade</Typography>
          <div className="actions">
            {Summarizer}
            <Button variant="contained" color="secondary" onClick={reset}>Cancel</Button>
            <Button data-test-id="confirm-add-trade-button" variant="contained" color="primary" disabled={tradeDisabled} onClick={addTrade}>Add trade</Button>
          </div>
        </Box>
        <Grid container className="content">
          <Grid className={classNames("selected-position-block", { multiline: selectPosition && selectedPosition?.original })}>
            {!selectedPosition && visible && <UnifiedSearch
              variant="outlined"
              autoFocus
              account={account}
              modelId={modelId}
              searchType={RECENT_INSTRUMENT_SEARCH_TYPES.REBALANCE}
              itemsActions={({
                security: {
                  noDispatch: true,
                  mainAction: searchItem => () => {
                    const newSearchItem = {
                      ...searchItem,
                      ...(searchItem.assetClass === "swap" && { id: searchItem.ticker }),
                    }
                    selectPosition(newSearchItem)
                  },
                },
                swap: {
                  noDispatch: true,
                  mainAction: searchItem => () => {
                    selectPosition({ ...searchItem, id: searchItem.ticker })
                  },
                },
                index: {
                  noDispatch: true,
                  mainAction: searchItem => () => {
                    selectPosition({ ...searchItem, assetClass: "index" })
                  },
                },
              })}
              isOpen={visible}
              allowedTypes={["security", "swap", "index", "currency"]}
            />}
            {selectedPosition && <SelectedPosition iconClassName="clear-button" position={selectedPosition} onIconClick={() => setSelectedPosition(null)} />}
          </Grid>

          {selectedPosition && <InputWidget {...{ selectedPosition: mergedPositionWithAdv, isActive, value, setValue, field, setField, addTrade, workingEquity, tradeDisabled, isActiveTradingDisabled, hideEconomicExposure }} />}
        </Grid>
        {(tradeMessage || currentGrouping) && (
          <Alert severity="info">
            {tradeMessage || `Positions are currently grouped by ${currentGrouping.label}. Traded securities will appear in the trades panel, when drilled-down into their corresponding group, and in the ungrouped portfolio.`}
          </Alert>
        )}
      </Paper>
    </Grow>
  </div>
}

NewTrade.propTypes = {
  account: PropTypes.string,
  contributors: PropTypes.objectOf(CONTRIBUTOR),
  currentGrouping: PropTypes.object,
  isActive: PropTypes.bool,
  isActiveTradingDisabled: PropTypes.bool,
  isRunning: PropTypes.bool,
  modelId: PropTypes.string,
  newSimulateAction: PropTypes.func,
  selectedPositionWithAdv: CONTRIBUTOR_WITH_ADV,
  selectSearchItem: PropTypes.func,
  workingEquity: PropTypes.number,
  tradeDisabledReason: PropTypes.string,
  Summarizer: PropTypes.node,
  externalState: PropTypes.shape({
    selectedPosition: CONTRIBUTOR,
    tradedPositionField: PropTypes.arrayOf(PropTypes.string),
  }),
  onReset: PropTypes.func,
  onAddTradeClicked: PropTypes.func,
  hideEconomicExposure: PropTypes.bool,
  tradeMessage: PropTypes.string,
}
